import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { authApi, formatApiError, isAdminRole } from '../lib/api';
import { LogIn, UserPlus, Loader2 } from 'lucide-react';

const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: '', email: '', phone: '', password: '' });
    setMode('login');
    setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let res;
      if (mode === 'login') {
        res = await authApi.login(form.email, form.password);
      } else {
        res = await authApi.register({
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          password: form.password,
          role: 'parent',
        });
      }
      const { access_token, user } = res.data;
      onLogin(user, access_token);
      toast({
        title: `Welcome${user.name ? ', ' + user.name : ''}! 🎉`,
        description: isAdminRole(user.role) ? 'Admin access granted.' : 'You are now signed in.',
      });
      handleClose();
    } catch (err) {
      toast({
        title: mode === 'login' ? 'Login failed' : 'Registration failed',
        description: formatApiError(err),
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="login-modal">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            {mode === 'login' ? 'Login to Hurupu Pools' : 'Create Your Account'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {mode === 'login' ? 'Sign in to book sessions and manage your memberships.' : 'Register to book sessions at Hurupu Pools.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {mode === 'register' && (
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" value={form.name} onChange={update('name')} placeholder="Your name" required data-testid="login-name-input" />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{mode === 'login' ? 'Email or Member ID' : 'Email'}</Label>
            <Input id="email" type={mode === 'login' ? 'text' : 'email'} value={form.email} onChange={update('email')} placeholder={mode === 'login' ? 'you@example.com or HP-00001' : 'you@example.com'} required data-testid="login-email-input" />
          </div>

          {mode === 'register' && (
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" value={form.phone} onChange={update('phone')} placeholder="+91 ..." data-testid="login-phone-input" />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={form.password} onChange={update('password')} placeholder="••••••••" required minLength={6} data-testid="login-password-input" />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
            size="lg"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : mode === 'login' ? (
              <LogIn className="mr-2 h-5 w-5" />
            ) : (
              <UserPlus className="mr-2 h-5 w-5" />
            )}
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </Button>
        </form>

        <div className="text-center text-sm text-gray-500">
          {mode === 'login' ? (
            <>
              New here?{' '}
              <button onClick={() => setMode('register')} className="text-cyan-600 font-medium hover:underline" data-testid="switch-to-register">
                Create an account
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button onClick={() => setMode('login')} className="text-cyan-600 font-medium hover:underline" data-testid="switch-to-login">
                Sign in
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
